import i18n from "@/i18n";
import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import { fetchFeesOverview, fetchFeesBreakdown } from "../../fees/service/feesService";
import { feesOverview as mockFeesOverview, quickLinks } from "./dashboardMockData";

// Cycles through the same tone palette the rest of the dashboard uses —
// enough distinct hues for a typical school's class count before repeating.
const CLASS_COLORS = [
  "var(--tone-blue-fg)",
  "var(--tone-purple-fg)",
  "var(--tone-green-fg)",
  "var(--brand-orange)",
  "var(--tone-rose-fg)",
];

function formatCurrency(amount: number) {
  return `₹${amount.toLocaleString("en-IN")}`;
}

function formatRelativeTime(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return i18n.t("common:relTime.justNow");
  if (minutes < 60) return i18n.t("common:relTime.minAgo", { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return i18n.t("common:relTime.hrAgo", { count: hours });
  const days = Math.round(hours / 24);
  return i18n.t("common:relTime.dayAgo", { count: days });
}

/**
 * Real fee collection totals from the Fees module (see
 * institutionAdmin/fees). Falls back to the mock constant on error — e.g.
 * an institution with no fee structures configured yet shouldn't break the
 * dashboard — this is a deliberate, temporary fallback, not a long-term
 * mock dependency.
 */
async function loadFeesOverview(institutionId: number) {
  try {
    const overview = await fetchFeesOverview(institutionId);
    return {
      collected: formatCurrency(overview.collectedAmount),
      expected: formatCurrency(overview.expectedAmount),
      percent: overview.percent,
      collectedAmount: overview.collectedAmount,
      expectedAmount: overview.expectedAmount,
    };
  } catch {
    return mockFeesOverview;
  }
}

/** Expected vs collected per class for the academic year — feeds the fees card's class breakdown and filter. */
async function loadFeesByClass(institutionId: number, academicYearId?: number | null) {
  try {
    return await fetchFeesBreakdown(institutionId, { groupBy: "class", ...(academicYearId ? { academicYearId } : {}) });
  } catch {
    return [];
  }
}

/** Daily fee collection for the last `days` days (missing days filled with 0) — feeds the fees line chart. */
async function loadFeesSeries(institutionId: number, academicYearId?: number | null, days = 90) {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  try {
    const rows = await fetchFeesBreakdown(institutionId, { groupBy: "date", from: iso(from), to: iso(to), ...(academicYearId ? { academicYearId } : {}) });
    const byDay = new Map(rows.filter((r) => r.date).map((r) => [String(r.date).slice(0, 10), Number(r.collected) || 0]));
    return Array.from({ length: days }, (_, i) => {
      const date = iso(new Date(from.getTime() + i * 86400000));
      return { date, collected: byDay.get(date) ?? 0 };
    });
  } catch {
    return [];
  }
}

interface InstitutionDashboardResponse {
  institution: {
    id: number;
    name: string;
    code: string;
    organizationId: number | null;
    campusId: number | null;
    isIndependent: boolean;
  };
  counts: {
    totalStudents: number;
    totalTeachers: number;
    totalStaff: number;
    totalAcademicGroups: number;
  };
  studentsByClass: { academicGroupId: number; academicGroup: string; count: number; percent: number }[];
  recentAdmissions: { studentId: number; name: string; admissionDate: string; createdAt: string }[];
  upcomingBirthdays: UpcomingBirthday[];
  dashboardTemplate?: string;
  subscription: {
    id: number;
    status: string;
    isTrial: boolean;
    startDate: string;
    endDate: string;
    students: { current: number; max: number };
    credentialUsers: { current: number; max: number };
  } | null;
  inheritsSubscriptionFromOrg: boolean;
}

export interface UpcomingBirthday {
  type: "STUDENT" | "TEACHER" | "STAFF";
  id: number;
  name: string;
  photoUrl: string | null;
  detail: string | null;
  date: string;
  daysAway: number;
  turning: number;
}

export async function fetchInstitutionDashboardStats(
  institutionId: number,
  academicYearId?: number | null
): Promise<InstitutionDashboardResponse> {
  const { data } = await privateClient.get(INSTITUTION_ADMIN_ENDPOINTS.DASHBOARD.STATS(institutionId), {
    params: academicYearId ? { academicYearId } : undefined,
  });
  return data;
}

export async function fetchDashboardOverview(institutionId: number, academicYearId?: number | null) {
  const [real, feesOverview, feesSeries, feesByClass] = await Promise.all([
    fetchInstitutionDashboardStats(institutionId, academicYearId),
    loadFeesOverview(institutionId),
    loadFeesSeries(institutionId, academicYearId),
    loadFeesByClass(institutionId, academicYearId),
  ]);

  const statCards = [
    { label: "institutionAdmin.dashboard.stat.totalStudents", value: String(real.counts.totalStudents), tone: "blue", iconKey: "users" },
    { label: "institutionAdmin.dashboard.stat.totalTeachers", value: String(real.counts.totalTeachers), tone: "blue", iconKey: "user" },
    { label: "institutionAdmin.dashboard.stat.totalStaff", value: String(real.counts.totalStaff), tone: "purple", iconKey: "users" },
    { label: "institutionAdmin.dashboard.stat.academicGroups", value: String(real.counts.totalAcademicGroups), tone: "green", iconKey: "book" },
  ];

  const studentsByClass = real.studentsByClass.map((c, i) => ({
    label: c.academicGroup,
    value: c.count,
    percent: c.percent,
    colorVar: CLASS_COLORS[i % CLASS_COLORS.length],
  }));

  const recentActivities = real.recentAdmissions.map((a) => ({
    studentId: a.studentId,
    text: i18n.t("common:relTime.admitted", { name: a.name }),
    time: formatRelativeTime(a.createdAt),
  }));

  return {
    statCards,
    institution: real.institution,
    subscription: real.subscription,
    inheritsSubscriptionFromOrg: real.inheritsSubscriptionFromOrg,
    feesOverview,
    feesSeries,
    feesByClass,
    studentsByClass,
    recentActivities,
    upcomingBirthdays: real.upcomingBirthdays ?? [],
    dashboardTemplate: real.dashboardTemplate ?? "CLASSIC",
    quickLinks,
  };
}